"use client";

import React, { useState, useEffect } from 'react';
import styles from './AlternativeNamesDisplay.module.css'; // Dedicated CSS module for read-only display

/**
 * AlternativeNamesDisplay – A read-only component that displays the alternative names
 * for a content item. Each alternative name is an object:
 * { name: string, required: boolean, suggested: boolean }
 *
 * The component fetches the names from the API and displays them in a list.
 */
const AlternativeNamesDisplay = ({ contentId }) => {
  const [altNames, setAltNames] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch existing alternative names on mount (or when contentId changes)
  useEffect(() => {
    const fetchAlternativeNames = async () => {
      try {
        console.log(`Fetching alternative names for contentId: ${contentId}`);
        const response = await fetch(`/api/content/alternativenames?contentId=${contentId}`);
        if (!response.ok) {
          throw new Error('Failed to fetch alternative names');
        }
        const data = await response.json();
        if (data.success) {
          console.log('Fetched alternative names:', data.alternativeNames);
          // Ensure we have an array of objects
          let loadedNames = data.alternativeNames || [];
          if (Array.isArray(loadedNames) && loadedNames.length > 0) {
            loadedNames = loadedNames.map(item => {
              if (typeof item === 'string') {
                // Convert old string form into object with default flags
                return { name: item, required: false, suggested: false };
              } else {
                // Ensure each object has the required fields
                return {
                  name: item.name || '',
                  required: !!item.required,
                  suggested: !!item.suggested,
                };
              }
            });
          }
          setAltNames(loadedNames);
        } else {
          setError(data.message || 'Error fetching alternative names');
        }
      } catch (error) {
        console.error('Error fetching alternative names:', error);
        setError('Error fetching alternative names.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAlternativeNames();
  }, [contentId]);

  // After loading is complete, dispatch a resize event to trigger the parent's re-measurement.
  useEffect(() => {
    if (!isLoading) {
      // Delay briefly to allow the component's final layout to settle
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 50);
    }
  }, [isLoading]);

  if (isLoading) {
    return <p className={styles.loadingMessage}>Loading alternative names...</p>;
  }

  if (error) {
    return <p className={styles.error}>{error}</p>;
  }

  return (
    <div className={styles.alternativeNamesDisplayContainer}>
      <h1 className={styles.heading}>Alternative Names</h1>
      {altNames.length === 0 ? (
        <p className={styles.noNames}>No alternative names available.</p>
      ) : (
        <ul className={styles.namesList}>
          {altNames.map((item, index) => (
            <li key={index} className={styles.nameItem}>
              <span className={styles.nameText}>{item.name}</span>
              {item.required && (
                <span className={styles.requiredBadge}>Required</span>
              )}
              {item.suggested && (
                <span className={styles.suggestedBadge}>Suggested</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AlternativeNamesDisplay;
